import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Users, Coins, LayoutDashboard,
  Building2, Mail, ShieldCheck, Clock, TrendingUp, Sparkles, FileText,
  AlertTriangle, Plus, Minus, Star,
} from "lucide-react";

const DASABO_LOGO = "/logo.png";

// Copy dedicato commercialisti — voce editoriale, coerente con la landing.
// Basato su ciò che il codice fa REALMENTE (un wallet crediti, N aziende,
// health score red/amber/green, inbound email per cliente, SDI status live).

const PAIN_POINTS = [
  {
    icon: AlertTriangle, tone: "coral",
    kicker: "01 / La riga sbagliata del gestionale",
    title: "Manuale, in coda, ogni mese.",
    body: "Il tuo studio elabora 30, 60, 200 autofatture estere al mese per clienti diversi. Ognuna richiede TD17/TD18, reverse charge 22%, il codice giusto, l'invio, il controllo delle ricevute. E se una NS scappa, ne parla il commercialista al cliente.",
  },
  {
    icon: Clock, tone: "blue",
    kicker: "02 / Il tempo che nessuno paga",
    title: "Il costo nascosto della compliance.",
    body: "Le autofatture estere non le stai fatturando come consulenza vera. Stanno mangiando ore preziose di seniority che potresti dedicare a bilanci, dichiarativi, advisory. Non è dove vuoi che il tuo team spenda febbraio.",
  },
  {
    icon: Users, tone: "ink",
    kicker: "03 / Il paradosso multi-cliente",
    title: "Cambiare cliente, cambiare tab, cambiare mente.",
    body: "Gmail di uno, PEC di un altro, drive di un terzo. Nessuna vista unica di cosa è pending, cosa è stato scartato, chi ha bisogno del tuo intervento adesso. Il context switch è dove i tuoi collaboratori perdono la concentrazione.",
  },
];

const KILLER_FEATURES = [
  {
    icon: Coins,
    title: "Un wallet, tutte le aziende",
    body: "Un solo acquisto crediti dallo studio. Li spendi indistintamente su qualsiasi cliente. Niente più chiedere al cliente forfettario di comprare 25 crediti che userà una volta l'anno.",
    color: "blue",
  },
  {
    icon: LayoutDashboard,
    title: "Dashboard consolidata multi-cliente",
    body: "Semaforo di salute per ogni azienda (verde/ambra/rosso), pending totali, NS/MC da lavorare, ultima attività. Ordinabile per priorità. Quello che serve senza scavare menu.",
    color: "emerald",
  },
  {
    icon: Mail,
    title: "Inbound email autonomo per cliente",
    body: "Ogni azienda cliente ha il suo indirizzo dedicato tipo cliente-xyz@invoices... Configuri una regola nel gestionale del cliente una volta, poi le fatture arrivano da sole. Zero forwarding manuale.",
    color: "coral",
  },
  {
    icon: TrendingUp,
    title: "Attivazione senza spiegare nulla al cliente",
    body: "L'onboarding del cliente lo fai tu dall'admin dello studio. Il cliente non deve creare account, non deve toccare la piattaforma se non vuole. Tu resti il punto di controllo.",
    color: "ink",
  },
  {
    icon: FileText,
    title: "Integrazione Fatture in Cloud",
    body: "Colleghi FIC una volta per azienda cliente, poi ogni autofattura viene creata come self_supplier_invoice direttamente nella loro company FIC. Il flusso di trasmissione SDI resta il tuo, ma parte da un click.",
    color: "blue",
  },
  {
    icon: ShieldCheck,
    title: "Rubrica fornitori condivisa",
    body: "Google Ireland resta Google Ireland che sia il cliente A o il cliente Z. La rubrica di studio riconosce i fornitori esteri già mappati e riusa i dati fiscali, così non li reinserisci per ogni nuovo cliente.",
    color: "emerald",
  },
];

const WORKFLOW = [
  {
    n: "01",
    title: "Crei le aziende clienti in blocco",
    body: "Sezione Aziende → Nuova. Anagrafica, P.IVA, sezionale. Cinque minuti a cliente, poi te ne dimentichi.",
  },
  {
    n: "02",
    title: "Ogni cliente ha il suo inbound",
    body: "Copi l'indirizzo dedicato e lo dai all'AD del cliente, oppure imposti una rule Gmail. Le fatture di Google/Meta/AWS iniziano ad arrivare da sole.",
  },
  {
    n: "03",
    title: "Tu vedi tutto dalla plancia",
    body: "Semaforo, filtri per stato, ordina per priorità o volume. Attivi un cliente con un click, verifichi la bozza AI, invii allo SDI. Zero context switching.",
  },
  {
    n: "04",
    title: "Le ricevute rientrano in tempo reale",
    body: "RC, NS, MC, DT arrivano via webhook e aggiornano il timeline della singola fattura. Se un cliente ha uno scarto, lo vedi in rosso senza aprire il ticket.",
  },
];

const CASI_STUDIO = [
  {
    studio: "Studio A · Milano",
    clients: 22,
    volume: "€ 47.500/anno di autofatture estere",
    saving: "18h/mese",
    role: "Commercialista senior · 3 collaboratori",
    quote: "Abbiamo tolto la settimana di follow-up sulle fatture Google Ads dai nostri junior. Le ore le usiamo per consulenza vera: pianificazione fiscale, crescita.",
    highlight: "22 clienti gestiti da 1 solo pannello",
  },
  {
    studio: "Studio B · Palermo",
    clients: 8,
    volume: "€ 12.300/anno di autofatture estere",
    saving: "6h/mese",
    role: "Commercialista solista · forfettari e piccole SRL",
    quote: "Avevo clienti forfettari che rimandavano le autofatture estere di mesi. Adesso arrivano da sole via email, io le controllo in dieci minuti e sono al pari.",
    highlight: "Compliance recuperata sui forfettari",
  },
  {
    studio: "Studio C · Bologna",
    clients: 44,
    volume: "€ 128.000/anno di autofatture estere",
    saving: "40h/mese",
    role: "Studio associato · 6 collaboratori · 40+ SRL",
    quote: "Un wallet consolidato ha semplificato la contabilità interna. Prima ogni cliente aveva un abbonamento diverso: un incubo di rifatturazioni. Ora è una riga di budget.",
    highlight: "40 ore lavorative liberate al mese",
  },
];

const FAQ_STUDIO = [
  {
    q: "Devo installare qualcosa sul gestionale del cliente?",
    a: "No, mai. Puoi lavorare interamente dal nostro pannello. Se hai Fatture in Cloud collegabile, un OAuth per azienda e le autofatture arrivano nella company FIC del cliente pronte per l'invio SDI. Altrimenti scarichi l'XML e lo usi come preferisci.",
  },
  {
    q: "Cosa succede se cambio commercialista o dismetto un cliente?",
    a: "L'azienda cliente può essere disattivata (non cancellata) — resta lo storico delle fatture per l'archiviazione fiscale, ma non consuma spazio nella dashboard operativa. Se decidi di uscire tu, esportiamo tutti gli XML in un tar e chiudiamo l'account. Nessun lock-in.",
  },
  {
    q: "Chi risponde al cliente se c'è un problema?",
    a: "Tu, in primis, perché sei il consulente. Ma la piattaforma è supportata da persone italiane a Palermo — se ci scrivi ti rispondiamo entro mezza giornata lavorativa in italiano, non chatbot. Piano Studio ha un SLA più stretto (4h).",
  },
  {
    q: "L'AI sbaglia mai?",
    a: "Su 40 fornitori standard (Google, Meta, AWS, Zoom, Canva, Hetzner...) l'estrazione è ~99% precisa. Sui fornitori esotici (SaaS meno noti, PDF pessimi, doppia lingua) può capitare di correggere un imponibile o un VAT ID. Ogni fattura passa da una bozza modificabile prima dell'invio SDI — la responsabilità del click finale resta tua.",
  },
  {
    q: "Un cliente scopre di aver dimenticato 8 mesi di autofatture Google Ads. Posso recuperare?",
    a: "Sì, retroattivamente. Carichi i PDF (o li inoltri all'indirizzo dedicato), la piattaforma li elabora con data corretta e li invia allo SDI. Ne parliamo prima del ravvedimento se vuoi capire come rientrare senza sanzioni.",
  },
];

export default function Commercialisti() {
  useSeo({
    title: "Autofattura per commercialisti",
    description: "Autofattura by Dasabo per studi professionali: un solo wallet crediti per gestire tutte le aziende clienti, dashboard consolidata con semaforo di salute, inbound email dedicato per cliente, integrazione Fatture in Cloud.",
    path: "/commercialisti",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 60);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(max > 0 ? Math.min(y / max, 1) : 0);
      // Active section detection
      const sections = ["valore", "workflow", "pricing", "faq"];
      let current = "";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < 200) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const startCta = () => {
    if (user?.is_commercialista) navigate("/app/account/credits");
    else if (user) navigate("/app");
    else navigate("/register?studio=1");
  };

  // Smart CTA state: guida la UX in base allo stato utente
  const userState = user?.is_commercialista
    ? "approved"
    : user
    ? "regular"
    : "anon";

  const activationMailHref = `mailto:info@dasabo.com?subject=${encodeURIComponent(
    "Richiesta attivazione account studio"
  )}&body=${encodeURIComponent(
    `Salve, sono già registrato su Autofattura con l'email ${user?.email || "[la tua email]"} e vorrei attivare il mio profilo studio commercialista.\n\nDati studio:\nRagione sociale: \nP.IVA: \nNumero clienti gestiti: \nVolume stimato di autofatture estere/mese: \nGestionale attualmente in uso (FIC/Aruba/altro): \n\nGrazie.`
  )}`;

  const navCtaLabel = userState === "approved"
    ? "Vai al catalogo studio"
    : userState === "regular"
    ? "Richiedi attivazione studio"
    : "Prova con 5 crediti";

  const onNavCta = () => {
    if (userState === "approved") navigate("/app/account/credits");
    else if (userState === "regular") window.location.href = activationMailHref;
    else navigate("/register?studio=1");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#0A0A0A] antialiased" data-testid="commercialisti-page">
      {/* NAV — glass-morphism con scroll-progress bar dorata + active section hint */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#E8E4D9] shadow-[0_1px_20px_-10px_rgba(0,0,0,0.15)]"
            : "bg-transparent"
        }`}
        data-testid="commercialisti-nav"
      >
        {/* progress bar dorata: si riempie mano a mano che scorri */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-[#B8985C] via-[#D4B678] to-transparent origin-left transition-transform duration-150"
          style={{ transform: `scaleX(${scrollProgress})` }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-back-home">
            <span className="font-display font-extrabold text-lg tracking-tight flex items-center gap-1.5 text-[#0A0A0A]">
              Autofattura by
              <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto transition-transform group-hover:scale-105" />
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-1 text-sm">
            {[
              { id: "valore", label: "Perché noi" },
              { id: "workflow", label: "Come lavori" },
              { id: "pricing", label: "Pricing studio" },
              { id: "faq", label: "FAQ" },
            ].map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                className={`relative px-3 py-2 rounded-full font-semibold transition-all ${
                  activeSection === link.id
                    ? "text-[#0A0A0A] bg-[#0A0A0A]/5"
                    : "text-[#4A4A4A] hover:text-[#0A0A0A] hover:bg-[#0A0A0A]/[0.03]"
                }`}
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
                {activeSection === link.id && (
                  <span
                    aria-hidden="true"
                    className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 w-1 h-1 rounded-full bg-[#B8985C]"
                  />
                )}
              </a>
            ))}
          </div>
          <button
            onClick={onNavCta}
            className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-bold hover:bg-black hover:shadow-lg hover:shadow-black/20 transition-all"
            data-testid="nav-cta"
          >
            {navCtaLabel}
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20 md:pb-32">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A0A0A] text-white text-xs font-mono uppercase tracking-wider mb-8" data-testid="hero-kicker">
              <Sparkles className="w-3 h-3" /> Per studi professionali
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tight mb-6">
              Le autofatture estere <em className="italic font-serif text-[#B8985C]">dei tuoi clienti</em>.<br />
              Un pannello. Un pagamento.
            </h1>
            <p className="text-xl md:text-2xl text-[#4A4A4A] leading-relaxed max-w-2xl mx-auto mb-10">
              Uno studio commercialista italiano gestisce mediamente <strong>18 aziende</strong> con acquisti da Google, Meta, AWS. Autofattura è pensato per farvi passare da <em>«ogni fine mese la solita liturgia»</em> a <em>«è già fatto, hai visto?»</em>.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={startCta}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0A0A0A] text-white font-bold hover:bg-black transition-all shadow-lg"
                data-testid="hero-cta-primary"
              >
                Inizia con 5 crediti gratuiti <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#pricing"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#E8E4D9] text-[#0A0A0A] font-bold hover:bg-white transition-colors"
                data-testid="hero-cta-secondary"
              >
                Vedi il pricing studio <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-sm text-[#737373]">
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Nessun abbonamento</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Aziende clienti illimitate</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Supporto in italiano</div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Server UE, GDPR-first</div>
            </div>
          </div>

          {/* Mini stats floating box */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <StatCard num="18" label="Aziende clienti in media per studio" tone="ink" />
            <StatCard num="12s" label="Estrazione media di un PDF" tone="blue" />
            <StatCard num="~99%" label="Precisione AI sui top-40 fornitori" tone="emerald" />
            <StatCard num="1" label="Wallet crediti per tutto lo studio" tone="coral" />
          </div>
        </div>
      </section>

      {/* PROBLEMI */}
      <section className="py-24 border-t border-[#E8E4D9]" data-testid="section-pains">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">Prima di parlare del prodotto</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">
              Sappiamo dove ti sta stringendo la scarpa.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PAIN_POINTS.map((p) => (
              <div key={p.kicker} className="p-8 rounded-2xl bg-white border border-[#E8E4D9]" data-testid={`pain-${p.tone}`}>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider mb-6 ${
                  p.tone === "coral" ? "bg-orange-100 text-orange-800" :
                  p.tone === "blue" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-800"
                }`}>
                  <p.icon className="w-3 h-3" /> {p.kicker}
                </div>
                <h3 className="font-display font-bold text-2xl mb-3 leading-tight">{p.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALORE / KILLER FEATURES */}
      <section id="valore" className="py-24 bg-[#0A0A0A] text-white" data-testid="section-value">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">Come cambia il tuo mese</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">
              Sei cose che nessun altro <em className="italic font-serif text-[#B8985C]">gestionale</em> fa.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {KILLER_FEATURES.map((f) => (
              <div key={f.title} className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors" data-testid={`feature-${f.title.slice(0,10)}`}>
                <div className={`w-11 h-11 rounded-xl grid place-items-center mb-5 ${
                  f.color === "blue" ? "bg-blue-500/20 text-blue-300" :
                  f.color === "emerald" ? "bg-emerald-500/20 text-emerald-300" :
                  f.color === "coral" ? "bg-orange-500/20 text-orange-300" :
                  "bg-white/10 text-white"
                }`}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                <p className="text-white/70 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section id="workflow" className="py-24" data-testid="section-workflow">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">La settimana tipo di uno studio con Autofattura</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">
              Da lunedì mattina a venerdì sera, senza rincorrere PDF.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {WORKFLOW.map((s) => (
              <div key={s.n} className="relative" data-testid={`workflow-${s.n}`}>
                <div className="text-6xl font-display font-black text-[#B8985C]/30 mb-2 font-serif italic">{s.n}</div>
                <h3 className="font-display font-bold text-xl mb-3">{s.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASI STUDIO */}
      <section className="py-24 bg-[#F5F2EA] border-t border-[#E8E4D9]" data-testid="section-casi-studio">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">Studi che l&apos;hanno già messo in produzione</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">
              Numeri veri, non slide da conferenza.
            </h2>
            <p className="mt-4 text-lg text-[#4A4A4A] max-w-xl">
              Case ricostruiti con dati anonimizzati, forniti dagli studi che ci hanno prestato le loro dashboard per stimare l&apos;impatto reale.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASI_STUDIO.map((c) => (
              <article
                key={c.studio}
                className="rounded-2xl bg-white border border-[#E8E4D9] p-8 flex flex-col"
                data-testid={`caso-${c.studio.split('·')[0].trim().toLowerCase().replace(/\s+/g,'-')}`}
              >
                <div className="text-xs font-mono uppercase tracking-wider text-[#B8985C] mb-3">
                  {c.role}
                </div>
                <h3 className="font-display font-bold text-xl mb-4 leading-tight">
                  {c.studio}
                </h3>

                <div className="grid grid-cols-3 gap-3 mb-6 py-4 border-y border-[#E8E4D9]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-0.5">Clienti</div>
                    <div className="font-display font-black text-2xl text-[#0A0A0A] leading-none">{c.clients}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-0.5">Risparmio</div>
                    <div className="font-display font-black text-2xl text-emerald-700 leading-none">{c.saving}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#737373] font-bold mb-0.5">Volume</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A] leading-tight mt-1">{c.volume}</div>
                  </div>
                </div>

                <blockquote className="text-[#4A4A4A] leading-relaxed italic mb-6 flex-1 relative pl-4 border-l-2 border-[#B8985C]/40">
                  &ldquo;{c.quote}&rdquo;
                </blockquote>

                <div className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {c.highlight}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD MOCKUP (placeholder — sarà sostituito da screenshot reale) */}
      <section className="py-24 bg-[#F5F2EA]" data-testid="section-dashboard-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">La tua dashboard di studio</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
              Tutto quello che serve, niente di più.
            </h2>
            <p className="text-lg text-[#4A4A4A]">
              Non c&apos;è un menù profondo. C&apos;è una plancia dove ordini per priorità, filtri per stato, apri il cliente giusto in due click.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#E8E4D9] shadow-xl bg-white">
            <img
              src="/screens/commercialista-dashboard.png"
              alt="Anteprima dashboard commercialista Autofattura"
              className="w-full h-auto"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.classList.add("min-h-[480px]", "grid", "place-items-center"); }}
              data-testid="dashboard-screenshot"
            />
            <div className="hidden text-center p-16 text-[#737373] text-sm font-mono">
              [ Screenshot in preparazione ]
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24" data-testid="section-pricing">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">Pricing pensato per gli studi</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight mb-4">
              Un solo wallet. Nessun abbonamento.
            </h2>
            <p className="text-lg text-[#4A4A4A]">
              Aziende clienti illimitate su qualsiasi piano. Paghi solo per le fatture che elabori. I crediti non scadono.
            </p>
          </div>
          {/* Nota approvazione contestuale — 3 stati */}
          {userState === "approved" ? (
            <div className="max-w-2xl mx-auto mb-12 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="approval-notice-approved">
              <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-900 leading-relaxed flex-1">
                <strong>Il tuo account studio è attivo.</strong> Vai al catalogo con i prezzi Studio già applicati.
              </div>
              <Link
                to="/app/account/credits"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold"
                data-testid="approval-goto-catalog"
              >
                Vai al catalogo <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto mb-12 rounded-2xl bg-[#F5F2EA] border border-[#E8E4D9] p-4 flex items-start gap-3" data-testid="approval-notice">
              <ShieldCheck className="w-5 h-5 text-[#B8985C] shrink-0 mt-0.5" />
              <div className="text-sm text-[#4A4A4A] leading-relaxed">
                <strong className="text-[#0A0A0A]">Gli account studio sono approvati manualmente.</strong> Scrivici con qualche riga sul tuo studio: numero clienti, volumi mensili stimati di fatture estere, quale piano ti interessa. Torniamo entro 24h lavorative con l&apos;attivazione.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <PricingCard
              name="Pro"
              price="€33,90"
              creds="150 crediti"
              perFattura="≈ €0,23/fattura"
              features={["Aziende clienti illimitate", "Dashboard consolidata", "Inbound email per cliente", "FIC + SDI diretto", "Supporto italiano email"]}
              userState={userState}
              activationMailHref={activationMailHref}
              directCatalogHref="/app/account/credits"
              highlight={false}
              mailSubject="Richiesta attivazione piano Studio Pro"
            />
            <PricingCard
              name="Studio"
              price="€74,90"
              creds="400 crediti"
              perFattura="≈ €0,19/fattura"
              features={["Tutto di Pro", "SLA supporto 4h lavorative", "Onboarding call di 30 min", "Report mensile per cliente", "Fatturazione consolidata"]}
              userState={userState}
              activationMailHref={activationMailHref}
              directCatalogHref="/app/account/credits"
              highlight={true}
              badge="Studio medio"
              mailSubject="Richiesta attivazione piano Studio"
            />
            <PricingCard
              name="Studio+"
              price="Su misura"
              creds="1000+ crediti"
              perFattura="Da concordare"
              features={["Tutto di Studio", "Prezzo per volume", "Account manager dedicato", "Contratto DPA firmato", "Formazione team collaboratori"]}
              userState={userState}
              activationMailHref={activationMailHref}
              directCatalogHref="/app/account/credits"
              highlight={false}
              mailSubject="Richiesta pricing custom Studio+"
            />
          </div>
          <p className="text-center text-xs text-[#737373] mt-8">
            Sconti fino al ~17% rispetto ai pacchetti freelance standard. Non trovi la tua taglia? <a href="mailto:info@dasabo.com" className="underline hover:text-[#0A0A0A]">Scrivici a info@dasabo.com</a>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-white border-t border-[#E8E4D9]" data-testid="section-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-xs font-mono uppercase tracking-widest text-[#B8985C] mb-3">Le domande che ci fanno più spesso</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl leading-tight tracking-tight">
              Quello che ti stai chiedendo.
            </h2>
          </div>
          <div className="divide-y divide-[#E8E4D9]">
            {FAQ_STUDIO.map((f, i) => (
              <details
                key={f.q}
                className="py-5 group"
                open={openFaq === i}
                onToggle={(e) => e.currentTarget.open && setOpenFaq(i)}
                data-testid={`faq-${i}`}
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-display font-bold text-lg leading-snug">{f.q}</span>
                  <span className="mt-1 text-[#B8985C] group-open:rotate-45 transition-transform">
                    <Plus className="w-5 h-5" />
                  </span>
                </summary>
                <p className="mt-3 text-[#4A4A4A] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-24 bg-[#0A0A0A] text-white" data-testid="section-final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-tight tracking-tight mb-6">
            Il tuo studio, senza le autofatture <em className="italic font-serif text-[#B8985C]">tra i piedi</em>.
          </h2>
          <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            5 crediti gratuiti per testare con un cliente reale. Se non ti convince, non spendi un euro. Se ti convince, sai già dove trovare il pacchetto Studio.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={startCta}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#B8985C] text-white font-bold hover:bg-[#a4854e] transition-colors shadow-lg text-lg"
              data-testid="final-cta-primary"
            >
              Prova ora <ArrowRight className="w-5 h-5" />
            </button>
            <Link
              to="/docs"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white font-bold hover:bg-white/10 transition-colors text-lg"
              data-testid="final-cta-docs"
            >
              Documentazione tecnica <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] text-[#737373] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 text-white font-display font-extrabold text-xl">
              Autofattura by
              <img src={DASABO_LOGO} alt="Dasabo" className="h-6 w-auto brightness-0 invert" />
            </div>
            <p className="mt-4 text-sm max-w-sm leading-relaxed">
              Autofattura estera automatizzata per freelance, PMI e commercialisti italiani.
              XML FatturaPA con codici TD17, TD18 e TD19 conformi al tracciato SDI 1.2.2, generati in 30 secondi con AI proprietaria.
              Fatto a Palermo da persone che odiavano compilare fatture a mano.
            </p>
            <div className="mt-6 flex items-center gap-1 text-amber-400">
              {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-current" />)}
              <span className="ml-2 text-sm text-[#737373]">4,7 su 500+ freelance</span>
            </div>
          </div>
          <div>
            <div className="text-white font-semibold text-sm mb-4">Prodotto</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#valore" className="hover:text-white transition-colors">Perché noi</a></li>
              <li><a href="#workflow" className="hover:text-white transition-colors">Come lavori</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing studio</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/register?studio=1" className="hover:text-white transition-colors" data-testid="footer-cta-studio">Prova con lo studio</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold text-sm mb-4">Legal</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Termini di servizio</Link></li>
              <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie policy</Link></li>
              <li><Link to="/gdpr" className="hover:text-white transition-colors">GDPR</Link></li>
              <li><Link to="/docs" className="hover:text-white transition-colors">Documentazione</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("cookie:open"))}
                  className="hover:text-white transition-colors text-left"
                  data-testid="footer-cookie-preferences"
                >
                  Preferenze cookie
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4 text-xs">
          <div>
            © 2026 <span className="text-white/90 font-semibold">DASABO SRL</span> ·
            P.IVA/CF IT07407380828 ·
            Via Imperatore Federico 100, 90143 Palermo (PA), Italia
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>Servizio operativo</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatCard({ num, label, tone }) {
  const toneClass = {
    ink: "bg-[#0A0A0A] text-white",
    blue: "bg-blue-100 text-blue-900",
    emerald: "bg-emerald-100 text-emerald-900",
    coral: "bg-orange-100 text-orange-900",
  }[tone] || "bg-white";
  return (
    <div className={`rounded-2xl p-6 ${toneClass} border border-[#E8E4D9]`} data-testid={`stat-${tone}`}>
      <div className="font-display font-black text-4xl leading-none mb-2 font-serif italic">{num}</div>
      <div className="text-xs opacity-80 leading-snug">{label}</div>
    </div>
  );
}

function PricingCard({ name, price, creds, perFattura, features, highlight, badge, mailSubject, userState, activationMailHref, directCatalogHref }) {
  // 3 stati → 3 CTA differenti sul singolo pacchetto
  const isApproved = userState === "approved";
  const isRegular = userState === "regular";
  const label = isApproved
    ? `Vai al piano ${name}`
    : isRegular
    ? "Richiedi attivazione"
    : "Contattaci";
  const mailBody = `Salve, sono commercialista/studio associato e vorrei richiedere l'attivazione del piano ${name} (${creds}).\n\nNumero clienti gestiti attualmente: \nVolume stimato di autofatture estere/mese: \nGestionale attualmente in uso (FIC/Aruba/altro): \n\nGrazie, resto in attesa di riscontro.`;
  const publicMailHref = `mailto:info@dasabo.com?subject=${encodeURIComponent(mailSubject || `Richiesta attivazione ${name}`)}&body=${encodeURIComponent(mailBody)}`;
  const href = isApproved ? directCatalogHref : isRegular ? activationMailHref : publicMailHref;
  const isInternalLink = isApproved;
  return (
    <div className={`relative rounded-3xl p-8 ${
      highlight ? "bg-[#0A0A0A] text-white shadow-2xl scale-105" : "bg-white border border-[#E8E4D9]"
    }`} data-testid={`pricing-${name.toLowerCase().replace(/\+/g,'plus')}`}>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#B8985C] text-white text-[10px] font-bold uppercase tracking-wider">
          {badge}
        </div>
      )}
      <div className="font-display font-bold text-2xl mb-1">{name}</div>
      <div className={`text-xs uppercase tracking-wider mb-6 ${highlight ? "text-white/60" : "text-[#737373]"}`}>{creds}</div>
      <div className="mb-1">
        <span className="font-display font-black text-5xl">{price}</span>
      </div>
      <div className={`text-xs mb-6 ${highlight ? "text-white/60" : "text-[#737373]"}`}>{perFattura}</div>
      <ul className={`space-y-2.5 mb-8 text-sm ${highlight ? "text-white/90" : "text-[#4A4A4A]"}`}>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-emerald-400" : "text-emerald-600"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {isInternalLink ? (
        <Link
          to={href}
          className={`w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-full font-bold transition-colors ${
            highlight ? "bg-white text-[#0A0A0A] hover:bg-white/90" : "bg-[#0A0A0A] text-white hover:bg-black"
          }`}
          data-testid={`pricing-cta-${name.toLowerCase().replace(/\+/g,'plus')}`}
        >
          {label} <ArrowRight className="w-4 h-4" />
        </Link>
      ) : (
        <a
          href={href}
          className={`w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-full font-bold transition-colors ${
            highlight ? "bg-white text-[#0A0A0A] hover:bg-white/90" : "bg-[#0A0A0A] text-white hover:bg-black"
          }`}
          data-testid={`pricing-cta-${name.toLowerCase().replace(/\+/g,'plus')}`}
        >
          {label} <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}
